import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ProgressState,
  ProgressPhase,
  Conversation,
  VisualizationHtml,
  ContextFile,
  AdditionalContextJson,
  GraphInterrupt,
} from '@/common/components/wavvest-planning/types';

export interface PdfProcessingProgress {
  isProcessing: boolean;
  currentFile?: string;
  currentStep?: string;
  totalFiles?: number;
  currentFileIndex?: number;
  totalPages?: number;
  currentPage?: number;
  progress?: number;
  stats?: {
    textPages: number;
    ocrPages: number;
    failedPages: number;
  };
}

export interface CanvasState {
  visualization: VisualizationHtml;
  visualizationTitle: string;
  isLoading: boolean; // control if the message is being processing with backend
  isWaitingForResponse: boolean; // control if waiting for AI response after sending message
  progressState: ProgressState;
  progressPhase: ProgressPhase | null; // coarse streaming-progress phase while a response generates
  activeToolMessageIndex: number | null; // current run's in-place tool status message
  conversation: Conversation;
  pdfProcessingProgress: PdfProcessingProgress;
  webSearchEnabled: boolean;
  currentThreadId: string | null;
  checkpointRefreshToken: number;
  activeRunId: string | null;
  isCancellingRun: boolean;
  isLegacySession: boolean;
  // The graph interruption currently awaiting a user response, or null when none
  // is pending. Non-null means the conversation is paused on a question; it holds
  // both the details to render (question/options/kind/details) and the
  // identifiers needed to resume (id/token/checkpoint). See ENG-2713.
  pendingInterrupt: GraphInterrupt | null;
  // Whether a resume request for the pending interrupt is currently in flight.
  // Read by the composer/page to gate input while resuming, like isLoading.
  isResumeInFlight: boolean;
  // Error message from the last failed resume attempt, or null. On failure we
  // deliberately leave pendingInterrupt non-null so the interrupt card stays
  // mounted and its locally-held draft answer survives for retry (ENG-2713
  // retry criterion — the draft lives in the card, this status lives here).
  resumeError: string | null;
}

const initialState: CanvasState = {
  visualization: {
    sourceCode: '',
    fileName: '',
    presignedUrl: '',
  },
  visualizationTitle: '',
  isLoading: false,
  isWaitingForResponse: false,
  progressState: {
    percent: 0,
    status: 'normal',
  },
  progressPhase: null,
  activeToolMessageIndex: null,
  conversation: {
    messages: [],
  },
  pdfProcessingProgress: {
    isProcessing: false,
  },
  webSearchEnabled: false,
  currentThreadId: null,
  checkpointRefreshToken: 0,
  activeRunId: null,
  isCancellingRun: false,
  isLegacySession: false,
  pendingInterrupt: null,
  isResumeInFlight: false,
  resumeError: null,
};

export const canvasSlice = createSlice({
  name: 'canvas',
  initialState,
  reducers: {
    updateVisualization: (state, action: PayloadAction<Partial<VisualizationHtml>>) => {
      state.visualization = {
        ...state.visualization,
        ...action.payload,
      };
    },

    setVisualizationTitle: (state, action: PayloadAction<string>) => {
      state.visualizationTitle = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsWaitingForResponse: (state, action: PayloadAction<boolean>) => {
      state.isWaitingForResponse = action.payload;
    },
    setWebSearchEnabled: (state, action: PayloadAction<boolean>) => {
      state.webSearchEnabled = action.payload;
    },
    updateProgressState: (state, action: PayloadAction<Partial<ProgressState>>) => {
      state.progressState = {
        ...state.progressState,
        ...action.payload,
      };
    },
    setActiveRunId: (state, action: PayloadAction<string | null>) => {
      state.activeRunId = action.payload;
    },
    setIsCancellingRun: (state, action: PayloadAction<boolean>) => {
      state.isCancellingRun = action.payload;
    },
    setIsLegacySession: (state, action: PayloadAction<boolean>) => {
      state.isLegacySession = action.payload;
    },
    /**
     * Record a new pending graph interruption, replacing any interruption that
     * was already pending. Direct assignment ensures a stale interrupt never
     * lingers if the graph raises a fresh one before the previous was resolved.
     * Resume mechanics are reset so a fresh interrupt never inherits a prior
     * attempt's in-flight/error status.
     */
    setPendingInterrupt: (state, action: PayloadAction<GraphInterrupt>) => {
      state.pendingInterrupt = action.payload;
      state.isResumeInFlight = false;
      state.resumeError = null;
    },
    /**
     * Clear the pending interruption once it has been resolved (resumed), tearing
     * down its associated resume status so nothing lingers into the next turn.
     */
    clearPendingInterrupt: (state) => {
      state.pendingInterrupt = null;
      state.isResumeInFlight = false;
      state.resumeError = null;
    },
    /**
     * Mark whether a resume request is in flight. Starting a fresh attempt
     * (true) clears any prior error so a stale message never shows during retry.
     */
    setResumeInFlight: (state, action: PayloadAction<boolean>) => {
      state.isResumeInFlight = action.payload;
      if (action.payload) {
        state.resumeError = null;
      }
    },
    /**
     * Record the outcome of a resume attempt: an error message on failure, or
     * null to clear it. An error also drops the in-flight flag. pendingInterrupt
     * is intentionally left untouched so the card stays mounted and its draft
     * answer survives for retry (see resumeError doc on CanvasState).
     */
    setResumeError: (state, action: PayloadAction<string | null>) => {
      state.resumeError = action.payload;
      if (action.payload) {
        state.isResumeInFlight = false;
      }
    },
    incrementProgressPercentByNumber: (state, action: PayloadAction<number>) => {
      if (state.progressState.percent + action.payload < 100) {
        state.progressState.percent += action.payload;
      }
      return;
    },
    incrementProgressPercentRandomly: (
      state,
      action: PayloadAction<{ min: number; max: number }>,
    ) => {
      const { min, max } = action.payload;
      const randomNumber = Math.floor(Math.random() * (max - min + 1)) + min;
      if (state.progressState.percent + randomNumber < 100) {
        state.progressState.percent += randomNumber;
      }
      return;
    },
    resetProgressState: (state) => {
      state.progressState = {
        percent: 0,
        status: 'normal',
      };
    },
    /**
     * Advances the streaming-progress phase, never regressing: the displayed
     * phase only moves forward for the duration of a run. Holds the
     * "never regress" logic so callers can dispatch a raw candidate phase.
     */
    advanceProgressPhase: (state, action: PayloadAction<ProgressPhase>) => {
      const candidate = action.payload;
      state.progressPhase =
        state.progressPhase === null
          ? candidate
          : (Math.max(state.progressPhase, candidate) as ProgressPhase);
    },
    resetProgressPhase: (state) => {
      state.progressPhase = null;
    },
    setConversation: (state, action: PayloadAction<Conversation>) => {
      state.conversation = action.payload;
      state.activeToolMessageIndex = null;
    },
    addUserMessage: (state, action: PayloadAction<string>) => {
      // A user message starts a new turn. Preserve the prior run's final tool
      // status, but make the next tool call create a fresh status line.
      state.activeToolMessageIndex = null;
      state.conversation.messages.push({
        sender: 'human',
        content: action.payload,
      });
    },
    addAIMessage: (state, action: PayloadAction<string>) => {
      state.conversation.messages.push({
        sender: 'ai',
        content: action.payload,
      });
    },
    addSystemMessage: (state, action: PayloadAction<string>) => {
      state.conversation.messages.push({
        sender: 'system',
        content: action.payload,
      });
    },
    addToolMessage: (state, action: PayloadAction<string>) => {
      state.conversation.messages.push({
        sender: 'tool',
        content: action.payload,
      });
    },
    upsertToolStatusMessage: (state, action: PayloadAction<string>) => {
      const activeIndex = state.activeToolMessageIndex;
      if (activeIndex !== null && state.conversation.messages[activeIndex]?.sender === 'tool') {
        // Move the shared status line to the end as it changes so it remains
        // localized beside the latest streaming activity.
        state.conversation.messages.splice(activeIndex, 1);
      }

      state.conversation.messages.push({
        sender: 'tool',
        content: action.payload,
      });
      state.activeToolMessageIndex = state.conversation.messages.length - 1;
    },
    finalizeToolStatusMessage: (state) => {
      // Leave the final text in the conversation while ending the in-place
      // update lifecycle for this run.
      state.activeToolMessageIndex = null;
    },
    /**
     * update last message
     * append token received from websocket to the last message
     * @param state
     * @param action(token received from websocket)
     */
    updateLastMessage: (state, action: PayloadAction<string>) => {
      if (state.conversation.messages.length > 0) {
        const lastIndex = state.conversation.messages.length - 1;
        state.conversation.messages[lastIndex].content += action.payload;
      }
    },
    clearConversationMessages: (state) => {
      state.conversation.messages = [];
      state.activeToolMessageIndex = null;
    },
    updateConversationContextFiles: (state, action: PayloadAction<ContextFile[]>) => {
      state.conversation.contextFiles = action.payload;
    },
    addConversationContextFile: (state, action: PayloadAction<ContextFile>) => {
      state.conversation.contextFiles?.push(action.payload);
    },
    removeConversationContextFile: (state, action: PayloadAction<string>) => {
      state.conversation.contextFiles = state.conversation.contextFiles?.filter(
        (file) => file.fileName !== action.payload,
      );
    },
    updateConversationContextFileStatus: (
      state,
      action: PayloadAction<{
        fileName: string;
        updates: Partial<ContextFile>;
      }>,
    ) => {
      const { fileName, updates } = action.payload;
      const file = state.conversation.contextFiles?.find((f) => f.fileName === fileName);
      if (file) {
        Object.assign(file, updates);
      }
    },
    updateConversationAdditionalContextJson: (
      state,
      action: PayloadAction<AdditionalContextJson>,
    ) => {
      // Direct replacement instead of merge to support property deletion
      state.conversation.additionalContextJson = action.payload;
    },
    resetCanvas: (state) => {
      state.conversation = initialState.conversation;
      state.visualization = initialState.visualization;
      state.visualizationTitle = initialState.visualizationTitle;
      state.isLoading = initialState.isLoading;
      state.isWaitingForResponse = initialState.isWaitingForResponse;
      state.progressState = initialState.progressState;
      state.progressPhase = initialState.progressPhase;
      state.activeToolMessageIndex = initialState.activeToolMessageIndex;
      state.pdfProcessingProgress = initialState.pdfProcessingProgress;
      state.webSearchEnabled = initialState.webSearchEnabled;
      state.currentThreadId = initialState.currentThreadId;
      state.checkpointRefreshToken = initialState.checkpointRefreshToken;
      state.activeRunId = initialState.activeRunId;
      state.isCancellingRun = initialState.isCancellingRun;
      state.isLegacySession = initialState.isLegacySession;
      state.pendingInterrupt = initialState.pendingInterrupt;
      state.isResumeInFlight = initialState.isResumeInFlight;
      state.resumeError = initialState.resumeError;
    },
    updatePdfProcessingProgress: (
      state,
      action: PayloadAction<{ event: string; [key: string]: unknown }>,
    ) => {
      const { event, ...payload } = action.payload;

      switch (event) {
        case 'document_processing_start':
          state.pdfProcessingProgress = {
            isProcessing: true,
            totalFiles: payload.totalFiles,
            currentStep: `Starting processing ${payload.totalFiles} file(s)...`,
            progress: 0,
          };
          break;

        case 'pdf_processing_start':
          state.pdfProcessingProgress.currentFile = payload.filename;
          state.pdfProcessingProgress.currentStep = 'Starting PDF processing...';
          if (payload.fileIndex !== undefined) {
            state.pdfProcessingProgress.currentFileIndex = payload.fileIndex + 1;
          }
          break;

        case 'pdf_processing': {
          const fileKey = payload.fileKey?.split('/').pop() || payload.fileKey;
          state.pdfProcessingProgress.currentFile = fileKey;
          state.pdfProcessingProgress.currentStep = `Processing ${fileKey}...`;
          if (payload.fileIndex !== undefined) {
            state.pdfProcessingProgress.currentFileIndex = payload.fileIndex + 1;
            // Calculate progress based on file index if total files is known
            if (state.pdfProcessingProgress.totalFiles) {
              const fileProgress =
                (payload.fileIndex / state.pdfProcessingProgress.totalFiles) * 80; // Use 80% for file processing
              state.pdfProcessingProgress.progress = Math.max(
                state.pdfProcessingProgress.progress || 0,
                fileProgress,
              );
            }
          }
          break;
        }

        case 'pdf_download_start':
          state.pdfProcessingProgress.currentStep = 'Downloading PDF...';
          state.pdfProcessingProgress.progress = 10;
          break;

        case 'pdf_download_complete':
          state.pdfProcessingProgress.currentStep = 'PDF downloaded successfully';
          state.pdfProcessingProgress.progress = 20;
          break;

        case 'pdf_load_start':
          state.pdfProcessingProgress.currentStep = 'Loading PDF document...';
          state.pdfProcessingProgress.progress = 25;
          break;

        case 'pdf_load_complete':
          state.pdfProcessingProgress.currentStep = 'PDF loaded successfully';
          state.pdfProcessingProgress.totalPages = payload.totalPages;
          state.pdfProcessingProgress.progress = 30;
          break;

        case 'page_analysis_start':
          state.pdfProcessingProgress.currentStep = 'Analyzing pages...';
          state.pdfProcessingProgress.progress = 35;
          break;

        case 'page_analysis_complete':
          state.pdfProcessingProgress.currentStep = 'Page analysis complete';
          state.pdfProcessingProgress.stats = {
            textPages: payload.textPages,
            ocrPages: payload.ocrPages,
            failedPages: 0,
          };
          state.pdfProcessingProgress.progress = 40;
          break;

        case 'ocr_batch_start':
          state.pdfProcessingProgress.currentStep = `Processing ${payload.pageCount} pages with OCR...`;
          state.pdfProcessingProgress.progress = 45;
          break;

        case 'ocr_page_complete':
          if (payload.ocrProgress) {
            state.pdfProcessingProgress.progress = 45 + payload.ocrProgress * 0.3;
          }
          break;

        case 'ocr_batch_complete':
          state.pdfProcessingProgress.currentStep = 'OCR processing complete';
          state.pdfProcessingProgress.progress = 75;
          break;

        case 'xml_generation_start':
          state.pdfProcessingProgress.currentStep = 'Generating XML...';
          state.pdfProcessingProgress.progress = 80;
          break;

        case 'page_processing':
          state.pdfProcessingProgress.currentPage = payload.pageNumber;
          if (payload.progress) {
            state.pdfProcessingProgress.progress = 80 + payload.progress * 0.15;
          }
          break;

        case 'page_complete':
          state.pdfProcessingProgress.currentPage = payload.pageNumber;
          break;

        case 'pdf_processing_complete':
          // This is for individual file completion within convert_pdf_to_xml
          if (payload.textPages !== undefined) {
            state.pdfProcessingProgress.stats = {
              textPages: payload.textPages,
              ocrPages: payload.ocrPages,
              failedPages: payload.failedPages,
            };
          }
          break;

        case 'pdf_processed':
          // Individual PDF file has been processed
          if (payload.fileIndex !== undefined && state.pdfProcessingProgress.totalFiles) {
            const completedFiles = payload.fileIndex + 1;
            state.pdfProcessingProgress.currentStep = `Processed ${completedFiles} of ${state.pdfProcessingProgress.totalFiles} files`;
            // Update progress based on completed files
            const overallProgress = (completedFiles / state.pdfProcessingProgress.totalFiles) * 90; // Use 90% for file processing
            state.pdfProcessingProgress.progress = Math.max(
              state.pdfProcessingProgress.progress || 0,
              overallProgress,
            );
          }
          break;

        case 'document_processing_complete':
          // All files have been processed
          state.pdfProcessingProgress.currentStep = 'All files processed successfully';
          state.pdfProcessingProgress.progress = 100;
          break;

        case 'pdf_processing_error':
          state.pdfProcessingProgress.currentStep = `Error: ${payload.error}`;
          state.pdfProcessingProgress.isProcessing = false;
          break;

        default:
          break;
      }
    },
    resetPdfProcessingProgress: (state) => {
      state.pdfProcessingProgress = initialState.pdfProcessingProgress;
    },
    setCurrentThreadId: (state, action: PayloadAction<string | null>) => {
      if (state.currentThreadId !== action.payload) {
        state.activeRunId = null;
        state.isCancellingRun = false;
        // A pending interrupt belongs to its thread; switching or starting a new
        // session must not carry a stale question — or its resume status — into
        // the new thread.
        state.pendingInterrupt = null;
        state.isResumeInFlight = false;
        state.resumeError = null;
        state.activeToolMessageIndex = null;
      }
      state.currentThreadId = action.payload;
    },
    triggerCheckpointRefresh: (state, action: PayloadAction<number>) => {
      state.checkpointRefreshToken = action.payload;
    },
  },
});

export const {
  updateVisualization,
  setVisualizationTitle,
  setIsLoading,
  setIsWaitingForResponse,
  setWebSearchEnabled,
  updateProgressState,
  setActiveRunId,
  setIsCancellingRun,
  setIsLegacySession,
  setPendingInterrupt,
  clearPendingInterrupt,
  setResumeInFlight,
  setResumeError,
  incrementProgressPercentByNumber,
  incrementProgressPercentRandomly,
  setConversation,
  addUserMessage,
  addAIMessage,
  addSystemMessage,
  addToolMessage,
  upsertToolStatusMessage,
  finalizeToolStatusMessage,
  updateLastMessage,
  clearConversationMessages,
  resetCanvas,
  resetProgressState,
  advanceProgressPhase,
  resetProgressPhase,
  updateConversationContextFiles,
  updateConversationAdditionalContextJson,
  addConversationContextFile,
  removeConversationContextFile,
  updateConversationContextFileStatus,
  updatePdfProcessingProgress,
  resetPdfProcessingProgress,
  setCurrentThreadId,
  triggerCheckpointRefresh,
} = canvasSlice.actions;

export default canvasSlice.reducer;

// Selectors
export const selectWebSearchEnabled = (state: { canvas: CanvasState }) =>
  state.canvas.webSearchEnabled;

export const selectPendingInterrupt = (state: { canvas: CanvasState }) =>
  state.canvas.pendingInterrupt;

export const selectHasPendingInterrupt = (state: { canvas: CanvasState }) =>
  state.canvas.pendingInterrupt !== null;

export const selectIsResumeInFlight = (state: { canvas: CanvasState }) =>
  state.canvas.isResumeInFlight;

export const selectResumeError = (state: { canvas: CanvasState }) => state.canvas.resumeError;
